/**
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var ApiEvents = require('core/api_events');
var Behavior = require('../behavior');
var DateEnv = require('../env/date');
var DefaultClientMetadata = require('../env/default_client_metadata');
var di = require('../di');
var DocumentEnv = require('../env/document');
var enums = require('../constants/enums');
var eventCategoryEnums = require('../constants/event_category');
var EventEmitter = require('../event_emitter');
var EventEmitterEnums = require('../constants/event_emitter');
var EventType = require('../constants/event_types');
var Flux = require('../flux');
var Layer = require('../layer');
var Logger = require('../utils/logger');
var performanceEnv = require('core/env/performance');
var PromiseEnv = require('../lib/promise').Promise;
var RedirectUtil = require('../utils/redirect');
var Relay = require('./relay');
var Timeout = require('../utils/timeout');
var trackingPolicies = require('../constants/tracking_policies');
var uuid = require('optimizely-uuid');

var ClientMetadata = di.get('stores/client_metadata');
var GlobalStore = di.get('stores/global');
var SessionStore = di.get('stores/session');
var ViewStore = di.get('stores/view');
var PluginsStore = di.get('stores/plugins');
var LayerStore = di.get('stores/layer');
var LayerDataStore = di.get('stores/layer_data');
var ObservedRedirectStore = di.get('stores/observed_redirect');
var PendingRedirectStore = di.get('stores/pending_redirect');
var VisitorStore = di.get('stores/visitor');
var DirectiveStore = di.get('stores/directive');
var EventDataStore = di.get('stores/event_data');
var VisitorIdStore = di.get('stores/visitor_id');

var DEFAULT_REDIRECT_RELAY_MEDIUM = __DEFAULT_REDIRECT_RELAY_MEDIUM__;
var XDOMAIN = __XDOMAIN__;
var TRACK_AFTER_SYNC_TIMEOUT = __TRACK_AFTER_SYNC_TIMEOUT__;
var BACKFILLED_LAYER_INTEGRATION_STRING = __BACKFILLED_LAYER_INTEGRATION_STRING__;

/**
 * Calls all registered analytics tracker for the `onClientActivation` event
 * This happens everytime the a client activation happens, it does not contain layer state information
 * however it does contain whatever we know about the visitor as userFeatures
 *
 * @return {ClientActivationEventData|undefined}
 */
exports.trackClientActivation = function(): ClientActivationEventData|undefined {
  if (!DirectiveStore.shouldSendTrackingData()) {
    return;
  }

  var clientActivationEventData = createClientActivationEventData();
  // do not track anything in the BehaviorStore as this event type is only used for backend counting purposes
  // and doesn't represnet any user behavior

  if (__INVARIANT__) {
    require('../utils/invariant').isType('ClientActivationEventData', clientActivationEventData);
  }

  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('sendActivate');

  callTrackersDefiningHook('onClientActivation', clientActivationEventData);

  return clientActivationEventData;
};

/**
 * Tracks a custom event, usually invoked by the public api `trackEvent` method
 *
 * Creates a custom event, adds to behavior store, and calls "onCustomEvent" trackers.
 *
 * @param {string} eventName
 * @param {Object} [metadata]
 * @param {Event} [event]
 * @return {CustomEventData|undefined}
 */
exports.trackCustomEvent = function(eventName:string, metadata?:any, event?:EventSchema, properties?: object): CustomEventData|undefined {
  metadata = metadata || {};
  properties = properties || {};
  if (!event) {
    event = EventDataStore.getByApiName(eventName);
  }

  var customEventData = createCustomEventData(eventName, metadata, properties, event);

  if (__INVARIANT__) {
    require('../utils/invariant').isType('CustomEventData', customEventData);
  }

  var behaviorEvent:any = {
    name: eventName,
    type: EventType.CUSTOM,
    category: customEventData.eventCategory,
    // Behavior store expects revenue as a top-level option, not a tag
    tags: _.omit(customEventData.eventTags, 'revenue'),
  };

  if (!_.isUndefined(metadata.revenue)) {
    behaviorEvent.revenue = metadata.revenue;
  }

  // Always emit this event for preview purposes, but specify internalOnly if tracking is disabled.
  ApiEvents.emitAnalyticsEvent({
    name: event ? (event.name || event.apiName) : eventName,
    apiName: event ? event.apiName : undefined,
    type: EventType.CUSTOM,
    tags: _.omit(customEventData.eventTags, 'revenue'),
    category: customEventData.eventCategory,
    metrics: behaviorEvent.revenue ? {revenue: behaviorEvent.revenue} : {},
  }, !DirectiveStore.shouldSendTrackingData());

  if (!DirectiveStore.shouldSendTrackingData()) {
    return;
  }

  Behavior.addEvent(behaviorEvent);

  callTrackersDefiningHook('onCustomEvent', customEventData);

  return customEventData;
};

/**
 * Track a decision that has just been made on this page load, via trackLayerDecision hooks.
 *
 * Also persist to the Redirect Relay Medium to ensure that tracking occurs on
 * the post-redirect page.
 *
 * Redirect tracking in particular is pretty involved:
 *
 * [![(Redirect) Decision tracking](https://www.websequencediagrams.com/files/render?link=6pQtTmyz3cogeeGwMET24fpmiSUypgc8UfBZ3pRJCczq6n2BEjBI5G6l51hpSrJ4)](https://www.websequencediagrams.com/#open=684193)
 *
 * @param {Decision} decision
 * @param {DecisionTicket} decisionTicket
 * @param {Layer} [layer]
 */
exports.trackDecisionEvent = function(decision:Decision, decisionTicket:DecisionTicket, layer?:Layer) {
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.time('trackDecisionEvent');
  if (!layer) {
    layer = LayerDataStore.get(decision.layerId);
  }

  var decisionId = uuid.generate();
  Flux.dispatch(actionTypes.RECORD_LAYER_DECISION_EVENT_ID, {
    layerId: decision.layerId,
    pageId: decisionTicket.pageId,
    decisionId: decisionId,
  });

  var persistableData = createPersistableDecisionWithContext(decisionId, decision, decisionTicket, layer);
  if (__INVARIANT__) {
    require('../utils/invariant').isType('PersistableDecisionWithContext', persistableData);
  }

  // If pending redirect for this layer, send persistable data to Relay
  var willRedirect = PendingRedirectStore.isExpectingRedirect();
  var redirectLayerId = PendingRedirectStore.getLayerId();
  var layerDescription = Layer.description(layer);
  if (willRedirect && redirectLayerId === layer.id) {
    Relay.persist(persistableData, DEFAULT_REDIRECT_RELAY_MEDIUM);
    Logger.log('Relaying decision for redirect Campaign', layerDescription);
  }

  if (!DirectiveStore.shouldSendTrackingData()) {
    Logger.log('Analytics / Not tracking decision for Campaign', Layer.description(layer));
    return;
  }

  // TODO: Allow changes to specify other relay media (communicated via the PendingRedirectStore).
  var decisionData = createDecisionData(persistableData, decision.isLayerHoldback, decision.isLayerConcluded);
  if (__INVARIANT__) {
    require('../utils/invariant').isType('DecisionData', decisionData);
  }

  if (willRedirect && redirectLayerId === layer.id) {
    // Invoke pre-redirect trackers
    var timing = trackingPolicies.TrackLayerDecisionTimingFlags.preRedirectPolicy;
    decisionData.timing = timing;

    callTrackersSpecifyingDecisionTiming(
      timing,
      [trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_AND_TRACK_DURING_REDIRECT],
      decisionData
    );
    Logger.log('Called trackLayerDecision for redirect Campaign', layerDescription, decisionData);
  } else {
    // Invoke non-redirect trackers
    var timing = trackingPolicies.TrackLayerDecisionTimingFlags.nonRedirectPolicy;
    decisionData.timing = timing;

    callTrackersSpecifyingDecisionTiming(
      timing,
      [trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY],
      decisionData
    );
    Logger.log('Called trackLayerDecision for non-redirect Campaign', layerDescription, decisionData);
  }
  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('trackDecisionEvent');
};

/**
 * Tracks a decision that was made prior to this page load and persisted in the Redirect Relay Medium (if applicable).
 *
 * NB. It's conceivable that the post-redirect page was running a different Snippet or whatever, in which case we might load
 * relayed data in a format that we don't expect. If that occurs, we'll pass unexpected data into the tracker
 * hooks, which would then fail to track. And our APIs may return unexpected data to callers.
 *
 * @returns {Promise} Resolves when all trackLayerDecision hooks have been called
 */
exports.trackPostRedirectDecisionEvent = function(): Promise<any> {
  if (!DirectiveStore.shouldSendTrackingData()) {
    return PromiseEnv.resolve();
  }

  // This redirect has already been tracked, so don't track it again
  if (ObservedRedirectStore.hasTracked()) {
    return PromiseEnv.resolve();
  }

  var persistedData = ObservedRedirectStore.get();

  // No data to track
  if (!persistedData) {
    return PromiseEnv.resolve();
  }

  // Invoke post-redirect immediate trackers
  var decisionData = createDecisionData(persistedData, false);
  var timing = trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy;
  decisionData.timing = timing;
  callTrackersSpecifyingDecisionTiming(
    timing,
    [trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY],
    decisionData
  );

  if (XDOMAIN) {
    // Wait for originsSynced event (or timeout), then track
    var syncPromise = new PromiseEnv(function(resolve) {
      var unlistenToken = EventEmitter.on({
        filter: {
          type: EventEmitterEnums.TYPES.LIFECYCLE,
          name: 'originsSynced',
        },
        handler: function() {
          resolve();
          EventEmitter.off(unlistenToken);
        },
      });
    });
    var timeoutPromise = Timeout.makeTimeoutPromise(TRACK_AFTER_SYNC_TIMEOUT);
    return PromiseEnv.race([syncPromise, timeoutPromise])
      .then(function() {
          Logger.log('Calling trackers after successful sync');
        }, function(err) {
          Logger.warn('Calling trackers after failed sync:', err);
        })
      .then(function() {
        // Reconstruct the DecisionData object now that we have an up-to-date visitor profile.
        decisionData = createDecisionData(persistedData, false);
        // Invoke post-redirect after-sync trackers (e.g. tracker_optimizely)
        decisionData.timing = trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy;
        callTrackersSpecifyingDecisionTiming(
          trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy,
          [trackingPolicies.PostRedirectPolicies.TRACK_AFTER_SYNC],
          decisionData
        );
        Flux.dispatch(actionTypes.REGISTER_TRACKED_REDIRECT_DATA);
      })
      ['catch'](function(err) {
        Logger.error('Error when calling trackers after sync:', err);
      });
  } else {
    // Invoke post-redirect after-sync trackers (since there's nothing to sync)
    callTrackersSpecifyingDecisionTiming(
      trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy,
      [trackingPolicies.PostRedirectPolicies.TRACK_AFTER_SYNC],
      decisionData
    );
    Flux.dispatch(actionTypes.REGISTER_TRACKED_REDIRECT_DATA);
    return PromiseEnv.resolve();
  }
};

/**
 * Track a click event.
 *
 * Create a click event, add it to behavior store, and call "onClickEvent" trackers.
 *
 * @param {EventSchema} event
 * @return {ClickEventData|undefined}
 */
exports.trackClickEvent = function(event:EventSchema): ClickEventData|undefined {
  var clickEventData = createClickEventData(event);

  if (__INVARIANT__) {
    require('../utils/invariant').isType('ClickEventData', clickEventData);
  }

  var behaviorEvent = {
    name: event.apiName,
    type: event.eventType,
    category: clickEventData.eventCategory,
    tags: clickEventData.eventTags,
  };

  // Always emit this event for preview purposes, but specify internalOnly if tracking is disabled.
  ApiEvents.emitAnalyticsEvent({
    name: event.name || event.apiName,
    apiName: event ? event.apiName : undefined,
    type: event.eventType,
    category: clickEventData.eventCategory,
    tags: clickEventData.eventTags,
    metrics: {}, // for now click events never have metrics
  }, !DirectiveStore.shouldSendTrackingData());

  if (!DirectiveStore.shouldSendTrackingData()) {
    return;
  }

  Behavior.addEvent(behaviorEvent);

  callTrackersDefiningHook('onClickEvent', clickEventData);

  return clickEventData;
};

/**
 * Tracks the activation of a view.
 *
 * Create a view event, add it to the behavior store, and call "onPageActivated" trackers.
 *
 * @param {View} view
 * @param {ViewState} [viewState]
 * @return {ViewEventData|undefined} data for tracked view, or undefined if not tracked
 */
exports.trackViewActivation = function(view:View, viewState?:ViewState): ViewEventData|undefined {
  if (!viewState) {
    viewState = ViewStore.getViewState(view.id);
  }

  if (!viewState.isActive) {
    Logger.debug('Inactive view passed to `trackViewActivation`');
    return;
  }

  var viewEventData = createViewEventData(view, viewState);

  if (__INVARIANT__) {
    require('../utils/invariant').isType('ViewEventData', viewEventData);
  }

  // Always emit this event for preview purposes, but specify internalOnly if tracking is disabled.
  ApiEvents.emitAnalyticsEvent({
    name: view.name || viewEventData.pageApiName,
    apiName: viewEventData.pageApiName,
    type: EventType.PAGEVIEW,
    category: viewEventData.viewCategory,
    tags: viewEventData.eventTags,
    metrics: {}, // for now view_activated events never have metrics
  }, !DirectiveStore.shouldSendTrackingData());

  if (!DirectiveStore.shouldSendTrackingData()) {
    return;
  }

  Behavior.addEvent({
    name: viewEventData.pageApiName,
    type: EventType.PAGEVIEW,
    category: viewEventData.viewCategory,
    tags: viewEventData.eventTags,
  });

  Flux.dispatch(actionTypes.TRACK_VIEW_ACTIVATED_EVENT, {
    pageId: view.id,
    eventData: viewEventData,
  });

  (__PROFILE__ || __RUM_VERBOSE__) && performanceEnv.timeEnd('sendViewActivate');

  callTrackersDefiningHook('onPageActivated', viewEventData);

  return viewEventData;
};

/**
 * @param {string} eventApiName
 * @param {Object} eventMetadata
 * @param {Event} [event]
 * @return {CustomEventData}
 * @private
 */
function createCustomEventData(eventApiName:string, eventMetadata:any, eventProperties: object, event?:EventSchema): CustomEventData {
  var baseEventData = createUserActionEventData({
    activeViewStates: ViewStore.getActiveViewStates(),
    visitorProfile: VisitorStore.getVisitorProfile(),
    layerStates: LayerStore.getLayerStatesForAnalytics(),
  });

  var viewTags = (event && event.pageId)
    ? getViewTagsForEvent(event)
    : ViewStore.getActiveViewTags();

  // collapse eventTags over view tags
  var eventTags = _.extend({}, viewTags, eventMetadata);

  var eventCategory = (event && event.category)
    ? event.category
    : eventCategoryEnums.OTHER;

  return _.extend(baseEventData, {
    // Note: Custom events are NOT guaranteed to have a corresponding GAE entity
    eventEntityId: (event && event.id),
    eventApiName: eventApiName,
    eventCategory: eventCategory,
    eventTags: eventTags,
    eventProperties: eventProperties,
  });
}

/**
 * @param {View} view
 * @param {ViewState} viewState
 * @return {ViewEventData}
 * @private
 */
function createViewEventData(view:View, viewState:ViewState): ViewEventData {
  var baseEventData = createUserActionEventData({
    activeViewStates: ViewStore.getActiveViewStates(),
    visitorProfile: VisitorStore.getVisitorProfile(),
    layerStates: LayerStore.getLayerStatesForAnalytics(),
  });

  return _.extend(baseEventData, {
    pageId: view.id,
    pageApiName: (<any>view).apiName,
    viewCategory: (<any>view).category,
    eventTags: viewState.metadata,
  });
}

/**
 * @param {Event} event
 * @return {ClickEventData}
 * @private
 */
function createClickEventData(event:EventSchema): ClickEventData {
  var baseEventData = createUserActionEventData({
    activeViewStates: ViewStore.getActiveViewStates(),
    visitorProfile: VisitorStore.getVisitorProfile(),
    layerStates: LayerStore.getLayerStatesForAnalytics(),
  });

  // backward compatible way of getting the selector
  // TODO(jordan): refactor this to only use config once migration has happened
  var selector = (event.config && event.config.selector)
    ? event.config.selector
    : event.eventFilter.selector;

  var eventApiName = event.apiName;
  var eventCategory = event.category || eventCategoryEnums.OTHER;
  var eventEntityId = event.id;

  // in the case of click events the tags on the page serve as event tags
  var eventTags = getViewTagsForEvent(event);

  return _.extend(baseEventData, {
    eventApiName: eventApiName,
    eventCategory: eventCategory,
    eventEntityId: eventEntityId,
    eventTags: eventTags,
    pageId: event.pageId,
    selector: selector,
  });
}

/**
 * @return {ClientActivationEventData}
 * @private
 */
function createClientActivationEventData(): ClientActivationEventData {
  // client activation events occur before view activations and layer decisions
  // thus we will always have empty viewStates and layerStates
  // However, we do include visitorProfile information as that can be carried over
  // from the last session.
  var eventData = createUserActionEventData({
    activeViewStates: [],
    visitorProfile: VisitorStore.getVisitorProfile(),
    layerStates: LayerStore.getLayerStatesForAnalytics(),
  });

  return _.extend(eventData, {
    eventTags: {},
  });
}

/**
 * These are essentially the non-green fields in the redirects design doc:
 *   https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 *
 * @param {string} decisionId
 * @param {Decision} decision
 * @param {DecisionTicket} decisionTicket
 * @param {Layer} layer
 * @return {PersistableDecisionWithContext}
 */
function createPersistableDecisionWithContext(decisionId:string, decision:Decision, decisionTicket:DecisionTicket, layer:Layer): PersistableDecisionWithContext {
  var experimentName = null;
  var variationName = null;
  var experimentIntegrationSettings = null;

  if (decision.experimentId) {
    var experiment = _.find(layer.experiments, {id: decision.experimentId});
    if (experiment) {
      experimentName = experiment.name || null;
      experimentIntegrationSettings = experiment.integrationSettings;
      if (decision.variationId) {
        var variation = _.find(experiment.variations, {id: decision.variationId});
        if (variation) {
          variationName = variation.name || null;
        }
      }
    }
  }

  var referrer = RedirectUtil.getReferrer() || DocumentEnv.getReferrer();

  var data = {
    sessionId: SessionStore.getSessionId(),
    decisionTicketAudienceIds: decisionTicket.audienceIds,
    visitorId: VisitorIdStore.getRandomId(),
    decisionId: decisionId,
    activationId: GlobalStore.getActivationId(),
    namespace: GlobalStore.getNamespace(),
    timestamp: DateEnv.now(),
    pageId: decisionTicket.pageId || null,
    variationId: decision.variationId,
    variationName: variationName,
    experimentId: decision.experimentId,
    experimentName: experimentName,
    layerId: decision.layerId,
    layerName: layer.name,
    layerPolicy: layer.policy,
    accountId: GlobalStore.getAccountId(),
    projectId: GlobalStore.getProjectId(),
    revision: String(GlobalStore.getRevision()),
    clientName: ClientMetadata.getClientName(),
    clientVersion: ClientMetadata.getClientVersion(),
    referrer: referrer,
    integrationStringVersion: BACKFILLED_LAYER_INTEGRATION_STRING ? layer.integrationStringVersion : layer.integrationStringVersion || 1,
    // Note(tyler): Include layer/experiment settings but not global settings (which are snippet-specific)
    integrationSettings: _.extend({}, layer.integrationSettings, experimentIntegrationSettings),
  };

  if (__INVARIANT__) {
    require('../utils/invariant').isType('PersistableDecisionWithContext', data);
  }

  return data;
}

/**
 * These are essentially the green fields in the redirects design doc:
 *   https://confluence.sso.episerver.net/display/EXPENG/Design+Doc%3A+improved+redirect+tracking
 *
 * @param {PersistableDecisionWithContext} persistableData
 * @param {boolean} isLayerHoldback
 * @return {DecisionData}
 * @private
 */
function createDecisionData(persistableData:PersistableDecisionWithContext, isLayerHoldback:boolean, isLayerConcluded:boolean = false): DecisionData {
  var data = _.extend({}, persistableData, {
    isLayerHoldback: isLayerHoldback,
    isLayerConcluded: isLayerConcluded,
    isGlobalHoldback: false,

    // TODO(CJS-3580): Resave snippets and then remove these fallbacks.
    // Snippets older than 0.127.0 do not relay clientName, and are the only clients that fail to do so.
    // If our persistableData was relayed by such a snippet, we fall back to the default client name, which is okay
    // because snippets older than 0.127.0 did not support altering the client name from the default (CJS-3263).
    clientName: !_.isNull(persistableData.clientName) ? persistableData.clientName : DefaultClientMetadata.NAME,
    // Snippets older than ~0.134.0 do not relay integrationStringVersion for very old layers with effective
    // integrationStringVersion=1.
    integrationStringVersion: BACKFILLED_LAYER_INTEGRATION_STRING ? persistableData.integrationStringVersion : !_.isNull(persistableData.integrationStringVersion) ? persistableData.integrationStringVersion : 1,

    anonymizeIP: !_.isNull(GlobalStore.getAnonymizeIP()) ? GlobalStore.getAnonymizeIP() : undefined,

    // These are "incorrectly" computed from the post-relaying context,
    // but they're only referenced by tracker_avro which is no longer used in any customer's snippets.
    // TODO(CJS-2541): Drop these fields along with tracker_avro.
    activationId: GlobalStore.getActivationId(),
    decisionTicketAudienceIds: [],
    // This is "incorrectly" computed from the post-relaying context,
    // but it's no longer used in any customer's snippets thanks to __EXPERIMENTAL_AUTO_SESSION_ID__.
    // TODO(CJS-756, CJS-1695, CJS-2049): Drop this field and all the code that references it.
    sessionId: SessionStore.getSessionId(),
    // These are "incorrectly" computed from the post-relaying context,
    // but this is unlikely to matter thanks to Data Platform's support for visitor attribute restoration (within
    // a given session) and layerState restoration (within a given session, or across all sessions if
    // Easy Event Tracking is enabled for both the decision and the conversion).
    activeViewStates: [],
    userFeatures: getUserFeatures(VisitorStore.getVisitorProfile()),
    layerStates: LayerStore.getLayerStatesForAnalytics(),
  });

  if (__INVARIANT__) {
    require('../utils/invariant').isType('DecisionData', data);
  }

  return data;
}

/**
 * @param {Object} state
 * @param {Object} state.visitorProfile
 * @param {ViewState[]} state.activeViewStates
 * @param {LayerState[]} state.layerStates
 * @return {UserActionEventData}
 * @private
 */
function createUserActionEventData(state:any): any {
  var referrer = RedirectUtil.getReferrer() || DocumentEnv.getReferrer();

  var userActionEventData = {
    eventId: uuid.generate(),
    timestamp: DateEnv.now(),
    // Client information
    revision: GlobalStore.getRevision(),
    clientName: ClientMetadata.getClientName(),
    clientVersion: ClientMetadata.getClientVersion(),

    projectId: GlobalStore.getProjectId(),
    accountId: GlobalStore.getAccountId(),

    // activation loop id
    activationId: GlobalStore.getActivationId(),
    sessionId: SessionStore.getSessionId(),
    isGlobalHoldback: GlobalStore.isGlobalHoldback(),

    namespace: GlobalStore.getNamespace(),
    referrer: referrer,

    visitorId: VisitorIdStore.getRandomId(),

    // TODO make this an array of { view: <View>, viewState: <ViewState> } to allow consumers to
    // have access over additional View properties such as category
    activeViewStates: state.activeViewStates,
    layerStates: state.layerStates,
    userFeatures: getUserFeatures(state.visitorProfile),
  };

  return userActionEventData;
}

/**
 * @param {string} pageId
 * @return {ViewTag[]}
 * @private
 */
function getViewTags(pageId:string): ViewTag[] {
  var viewState = ViewStore.getViewState(pageId);

  var viewTags = (viewState && viewState.isActive)
    ? viewState.metadata
    : {};

  return viewTags;
}

/**
 * @param {Event} event
 * @return {ViewTag[]}
 * @private
 */
function getViewTagsForEvent(event:EventSchema): ViewTag[] {
  var viewTags = {} as any;
  if (!event.pageId) {
    return viewTags;
  }
  return getViewTags(event.pageId);
}

/**
 * Get current values for registered features for current profile
 * @param {module:core/stores/visitor~VisitorProfile} visitorProfile
 * @returns {FeatureValue[]}
 * @private
 */
function getUserFeatures(visitorProfile:import('core/stores/visitor').VisitorProfile): FeatureValue[] {
  var visitorProfileProviders = PluginsStore.getAllPlugins(enums.PluginTypes.visitorProfileProviders);
  var visitorFeatures = _.filter(visitorProfileProviders, {shouldTrack: true});

  // These values can be overridden by keys of the same name in the (a) featureDef (b) featureValue
  var defaults = {
    id: null,
    type: null,
    name: '',
    value: null,
    shouldIndex: true,
  };

  return _.reduce(visitorFeatures, function(res, feature) {
    try {
      var key = feature.provides;
      var value = visitorProfile[key];
      var values = [];

      if (!_.isUndefined(value)) {
        // there is some value for the provider
        if (_.isObject(value)) {
          // the value is an object, create one feature for each provided key
          // Note(tyler): a _single_ feature with id/name/etc is not supported
          values = _.map(value, function(v, k) {
            // if the feature does not define an object, create one with the value key set to the provided value
            var featureValue = _.isObject(v) ? v : {value: v};
            // set the default type (from provides) and name (from key)
            return _.extend({}, {
              type: key,
              name: k,
            }, featureValue);
          });
        } else {
          // create one feature for the value
          values.push({
            type: key,
            value: value,
          });
        }

        // augment each feature with the defaults and limit to defined keys
        var newFeatures = _(values)
          .map(function(v) {
            return _.pick(_.extend({}, defaults, v), _.keys(defaults));
          })
          .filter(function(v) {
            return !!v.value;
          })
          .value();

        if (__INVARIANT__) {
          _.forEach(newFeatures, function(fv) {
            require('../utils/invariant').isType('FeatureValue', fv);
          });
        }

        res = res.concat(newFeatures);
      }
    } catch (e) {
      Logger.warn('Error evaluating userFeature against visitorProfile:', e);
    }
    return res;
  }, []);
}

/**
 * @param {string} hook
 * @param {*} hookArg
 * @param {string} [disqualifyingHook] A hook whose definition disqualifies a tracker plugin from being called,
 *                                             even if the plugin defines the hook that we're looking for.
 * @private
 */
function callTrackersDefiningHook(hook:string, hookArg:any, disqualifyingHook?:string) {
  var plugins = trackersDefiningHook(hook, disqualifyingHook);

  Logger.debug('Found ' + plugins.length + ' analytics integrations defining a ' + hook + ' hook');
  Logger.debug('Calling each with data: ', hookArg);

  _.each(plugins, function(plugin) {
    try {
      Logger.debug('Calling plugin: ' + plugin.name);
      plugin.hookFn(hookArg);
      Logger.debug('Called plugin: ' + plugin.name);
    } catch(e) {
      Logger.error(e);
    }
  });
}

/**
 * @param {string} hook
 * @param {string} [disqualifyingHook] A hook whose definition disqualifies a tracker plugin from being called,
 *                                             even if the plugin defines the hook that we're looking for.
 * @return {Object[]} { name: string, hookFn: Function } Analytics tracking plugins
 *                    that define handlers for the given hook
 * @private
 */
function trackersDefiningHook(hook:string, disqualifyingHook?:string): any[] {
  if (__INVARIANT__) {
    require('../utils/invariant').isType('AnalyticsTrackingHook', hook);
  }

  var res = [];

  _.each(PluginsStore.getAllPlugins(enums.PluginTypes.analyticsTrackers), function(tracker, name) {
    if (tracker[hook] && (!disqualifyingHook || !tracker[disqualifyingHook])) {
      try {
        res.push({
          name: name,
          hookFn: di.evaluate(tracker[hook]),
        });
      } catch(e) {
        Logger.error(e);
      }
    }
  });

  return res;
}

/**
 * Call the .trackLayerDecision hook of every tracker that defines an acceptable value for the specified flag.
 * @private
 * @param {string} flagName E.g. 'preRedirectPolicy', 'postRedirectPolicy', or 'nonRedirectPolicy'.
 * @param {string[]} acceptableFlagValues E.g. ['TRACK_IMMEDIATELY', 'TRACK_AFTER_SYNC']
 * @param {*} hookArg
 */
function callTrackersSpecifyingDecisionTiming(flagName:string, acceptableFlagValues:string[], hookArg:any) {
  var plugins = trackersSpecifyingDecisionTiming(flagName, acceptableFlagValues);

  Logger.debug(
    'Found ' + plugins.length + ' analytics integrations ' +
    ' defining a trackLayerDecision ' + flagName + ' timing of ' + acceptableFlagValues.join('|'));
  Logger.debug('Calling each with data: ', hookArg);

  _.each(plugins, function(plugin) {
    try {
      Logger.debug('Calling plugin: ' + plugin.name);
      plugin.hookFn(hookArg);
      Logger.debug('Called plugin: ' + plugin.name);
    } catch(e) {
      Logger.error(e);
    }
  });
}

/**
 * @private
 * @param {string} flagName E.g. 'preRedirectPolicy', 'postRedirectPolicy', or 'nonRedirectPolicy'.
 * @param {string[]} acceptableFlagValues E.g. ['TRACK_IMMEDIATELY', 'TRACK_AFTER_SYNC']
 * @return {Object[]} { name: string, hookFn: Function } Analytics tracking plugins
 *                    that define handlers for the given hook
 */
function trackersSpecifyingDecisionTiming(flagName:string, acceptableFlagValues:string[]): any[] {
  if (__INVARIANT__) {
    if (flagName === trackingPolicies.TrackLayerDecisionTimingFlags.preRedirectPolicy) {
      _.forEach(acceptableFlagValues, function(flagValue) {
        if (!trackingPolicies.PreRedirectPolicies[flagValue]) {
          throw new Error('' + flagValue + ' is not a recognized ' + flagName);
        }
      });
    } else if (flagName === trackingPolicies.TrackLayerDecisionTimingFlags.postRedirectPolicy) {
      _.forEach(acceptableFlagValues, function(flagValue) {
        if (!trackingPolicies.PostRedirectPolicies[flagValue]) {
          throw new Error('' + flagValue + ' is not a recognized ' + flagName);
        }
      });
    } else if (flagName === trackingPolicies.TrackLayerDecisionTimingFlags.nonRedirectPolicy) {
      _.forEach(acceptableFlagValues, function(flagValue) {
        if (!trackingPolicies.NonRedirectPolicies[flagValue]) {
          throw new Error('' + flagValue + ' is not a recognized ' + flagName);
        }
      });
    } else {
      throw new Error('' + flagName + ' is not a recognized timing flag for tracker plugins');
    }
  }

  var res = [];

  _.each(PluginsStore.getAllPlugins(enums.PluginTypes.analyticsTrackers), function(tracker, name) {
    if (_.includes(acceptableFlagValues, tracker[flagName])) {
      res.push({
        name: name,
        hookFn: tracker.trackLayerDecision,
      });
    }
  });

  return res;
}

if (__TEST__) {
  exports.getUserFeatures = getUserFeatures;

  module.exports.setDefaultRedirectRelayMedium = function(testValue) {
    DEFAULT_REDIRECT_RELAY_MEDIUM = testValue;
  };
  module.exports.setXDomain = function(testValue) {
    XDOMAIN = testValue;
  };
  module.exports.setTrackAfterSyncTimeout = function(testValue) {
    TRACK_AFTER_SYNC_TIMEOUT = testValue;
  };
}
